import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { Shield, Youtube, Send, CheckCircle2, Eye } from "lucide-react";

export default function Copyright() {
  const [songs, setSongs] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ submission_id: "", youtube_url: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/submissions").then((r) => r.data),
      api.get("/copyright").then((r) => r.data),
    ]);
    setSongs(s); setRecords(c);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.submission_id) { toast.error("Please select a song"); return; }
    setLoading(true);
    try {
      await api.post("/copyright", form);
      toast.success("Copyright request submitted");
      setForm({ submission_id: "", youtube_url: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submit failed");
    } finally { setLoading(false); }
  };

  const selectedSong = songs.find((s) => s.id === form.submission_id);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Rights Protection</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Copyright</h1>
      <p className="mt-2 text-zinc-500 text-sm">Submit a YouTube link and our team will handle the takedown workflow.</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-2 p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 h-fit">
          <h2 className="text-lg font-semibold mb-5 flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /> New Request</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Select Song</Label>
              <Select value={form.submission_id} onValueChange={(v) => setForm({ ...form, submission_id: v })}>
                <SelectTrigger data-testid="cr-song-select" className="mt-1.5 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Choose one of your songs" /></SelectTrigger>
                <SelectContent>
                  {songs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.song_title} — {s.primary_artist_name || s.artist_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSong && (
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-zinc-900 border border-zinc-800"><span className="text-zinc-500">ISRC</span> <span className="font-mono text-emerald-400 ml-1">{selectedSong.isrc || "—"}</span></div>
                  <div className="p-2 rounded bg-zinc-900 border border-zinc-800"><span className="text-zinc-500">UPC</span> <span className="font-mono text-emerald-400 ml-1">{selectedSong.upc || "—"}</span></div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">YouTube Video URL</Label>
              <div className="relative mt-1.5">
                <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rose-400" />
                <Input required data-testid="cr-youtube-url" value={form.youtube_url}
                  onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..." className="bg-zinc-900 border-zinc-800 pl-9" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Notes / Message</Label>
              <Textarea rows={3} data-testid="cr-notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-zinc-800" placeholder="Describe the issue..." />
            </div>
            <Button type="submit" disabled={loading} data-testid="cr-submit"
              className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
              <Send className="w-4 h-4 mr-2" /> {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </form>

        {/* History */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
            <div className="p-6 border-b border-zinc-800">
              <h2 className="text-lg font-semibold">Request History</h2>
            </div>
            {records.length === 0 ? (
              <div className="p-12 text-center text-zinc-500">No copyright requests yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                      <th className="p-3">Request ID</th>
                      <th className="p-3">Song</th>
                      <th className="p-3">YouTube</th>
                      <th className="p-3">Submitted</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Last Updated</th>
                      <th className="p-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} data-testid={`cr-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                        <td className="p-3 font-mono text-cyan-400 text-sm">{r.id}</td>
                        <td className="p-3">
                          <div className="font-medium truncate max-w-[180px]">{r.song_title}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{r.isrc || "—"}</div>
                        </td>
                        <td className="p-3">
                          <a href={r.youtube_url} target="_blank" rel="noreferrer"
                            className="text-rose-400 hover:underline inline-flex items-center gap-1 text-xs">
                            <Youtube className="w-3.5 h-3.5" /> Open
                          </a>
                        </td>
                        <td className="p-3 text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <StatusBadge status={r.status} />
                            {r.status === "Removed / Resolved" && (
                              <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px]">
                                <CheckCircle2 className="w-3 h-3" /> OK – Resolved
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-xs text-zinc-500">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost" className="text-cyan-400 h-7"
                            onClick={() => setDetails(r)} data-testid={`cr-view-${r.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!details} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="cr-details-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-cyan-400" /> Copyright Request</DialogTitle></DialogHeader>
          {details && (
            <div className="space-y-3 text-sm">
              <D k="Request ID" v={<code className="text-cyan-400">{details.id}</code>} />
              <D k="Song" v={details.song_title} />
              <D k="ISRC" v={<code className="text-emerald-400">{details.isrc || "—"}</code>} />
              <D k="UPC" v={<code className="text-emerald-400">{details.upc || "—"}</code>} />
              <D k="Status" v={<StatusBadge status={details.status} />} />
              <D k="Submitted" v={new Date(details.created_at).toLocaleString()} />
              <D k="Last Updated" v={details.updated_at ? new Date(details.updated_at).toLocaleString() : "—"} />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">YouTube URL</div>
                <a href={details.youtube_url} target="_blank" rel="noreferrer" className="text-rose-400 hover:underline break-all inline-flex items-center gap-1">
                  <Youtube className="w-3.5 h-3.5 shrink-0" /> {details.youtube_url}
                </a>
              </div>
              {details.notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Your Note</div>
                  <div className="p-3 rounded bg-zinc-900 border border-zinc-800 text-zinc-300">{details.notes}</div>
                </div>
              )}
              {details.admin_notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-400 mb-1">Admin Reply</div>
                  <div className="p-3 rounded bg-cyan-500/5 border border-cyan-500/30 text-zinc-200">{details.admin_notes}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function D({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-zinc-500 uppercase tracking-wider text-xs">{k}</span>
      <span className="text-zinc-200 text-right">{v || "—"}</span>
    </div>
  );
}
